import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Calendar, Users, ListMusic, Settings, Plus, ExternalLink, Trash2, Eye, EyeOff,
  Check, X, UserPlus, Pencil, StickyNote, ChevronDown, ChevronUp, Ban, Guitar,
  CalendarOff, Repeat,
} from 'lucide-react';
import { useBand } from '../../hooks/useBands';
import { useBandEvents, deleteEvent } from '../../hooks/useEvents';
import { useBandSetlists, deleteSetlist } from '../../hooks/useSetlists';
import { useBandJoinRequests, approveJoinRequest, rejectJoinRequest } from '../../hooks/useJoinRequests';
import { useBandUnavailability, deleteUnavailability } from '../../hooks/useUnavailability';
import { useAuth } from '../../hooks/useAuth';
import { formatDate, formatTime, getInitials } from '../../lib/utils';
import InviteMemberModal from '../../components/band/InviteMemberModal';
import CreateEventModal from '../../components/band/CreateEventModal';
import CreateSetlistModal from '../../components/band/CreateSetlistModal';
import EditBandModal from '../../components/band/EditBandModal';
import EditEventModal from '../../components/band/EditEventModal';
import EditInstrumentsModal from '../../components/band/EditInstrumentsModal';
import AddUnavailabilityModal from '../../components/band/AddUnavailabilityModal';
import AddRehearsalModal from '../../components/band/AddRehearsalModal';
import BandCalendarView from '../../components/band/BandCalendarView';
import type { Event, BandMember } from '../../types/database';

type Tab = 'calendar' | 'members' | 'setlists' | 'settings';
type CalendarMode = 'calendar' | 'list';

export default function ManageBandPage() {
  const { bandId } = useParams<{ bandId: string }>();
  const { band, members, loading, userRole, refetch: refetchBand } = useBand(bandId);
  const { events, refetch: refetchEvents } = useBandEvents(bandId, true);
  const { setlists, refetch: refetchSetlists } = useBandSetlists(bandId, true);
  const { requests: joinRequests, refetch: refetchJoinRequests } = useBandJoinRequests(bandId);
  const { unavailability, refetch: refetchUnavailability } = useBandUnavailability(bandId);
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('calendar');
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('calendar');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [joinActionLoading, setJoinActionLoading] = useState<string | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventModalInitialDate, setEventModalInitialDate] = useState<string | undefined>();
  const [showSetlistModal, setShowSetlistModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [showUnavailabilityModal, setShowUnavailabilityModal] = useState(false);
  const [showRehearsalModal, setShowRehearsalModal] = useState(false);
  const [editingInstruments, setEditingInstruments] = useState<BandMember | null>(null);
  const [myUnavailExpanded, setMyUnavailExpanded] = useState(false);

  const isAdmin = userRole === 'owner' || userRole === 'admin';

  // Get current user's unavailability entries
  const myUnavailability = unavailability.filter((u) => u.user_id === user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Band Not Found</h1>
        <p className="text-gray-400">You may not have access to this band.</p>
      </div>
    );
  }

  async function handleDeleteEvent(eventId: string) {
    if (!confirm('Are you sure you want to delete this event?')) return;
    await deleteEvent(eventId);
    refetchEvents();
  }

  async function handleApproveJoin(req: typeof joinRequests[0]) {
    if (!user) return;
    setJoinActionLoading(req.id);
    await approveJoinRequest(req.id, req.band_id, req.user_id, user.id);
    refetchJoinRequests();
    refetchBand();
    setJoinActionLoading(null);
  }

  async function handleRejectJoin(req: typeof joinRequests[0]) {
    if (!user) return;
    setJoinActionLoading(req.id);
    await rejectJoinRequest(req.id, user.id);
    refetchJoinRequests();
    setJoinActionLoading(null);
  }

  async function handleDeleteSetlist(setlistId: string) {
    if (!confirm('Are you sure you want to delete this setlist?')) return;
    await deleteSetlist(setlistId);
    refetchSetlists();
  }

  async function handleDeleteUnavailability(id: string) {
    await deleteUnavailability(id);
    refetchUnavailability();
  }

  function handleCalendarAddEvent(date: string) {
    setEventModalInitialDate(date);
    setShowEventModal(true);
  }

  const tabs: { key: Tab; label: string; icon: typeof Calendar }[] = [
    { key: 'calendar', label: 'Calendar', icon: Calendar },
    { key: 'members', label: 'Members', icon: Users },
    { key: 'setlists', label: 'Setlists', icon: ListMusic },
    { key: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Band header */}
      <div className="flex flex-col sm:flex-row items-start gap-4 mb-8">
        {band.logo_url ? (
          <img src={band.logo_url} alt={band.name} className="w-16 h-16 rounded-lg object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-gray-800 flex items-center justify-center">
            <span className="text-gray-400 font-bold text-lg">{getInitials(band.name)}</span>
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{band.name}</h1>
            <Link
              to={`/band/${band.slug}`}
              className="text-gray-400 hover:text-indigo-400 transition"
              title="View public page"
            >
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-gray-400 text-sm">
            {band.location_name}{band.location_country && `, ${band.location_country}`}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-gray-800 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-3 font-medium text-sm border-b-2 transition whitespace-nowrap ${
              activeTab === key
                ? 'border-indigo-400 text-indigo-400'
                : 'border-transparent text-gray-400 hover:text-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Events</h2>
              <div className="flex bg-gray-800 rounded-lg p-0.5 text-sm">
                <button
                  onClick={() => setCalendarMode('calendar')}
                  className={`px-3 py-1 rounded-md font-medium transition ${
                    calendarMode === 'calendar' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCalendarMode('list')}
                  className={`px-3 py-1 rounded-md font-medium transition ${
                    calendarMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <ListMusic className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowUnavailabilityModal(true)}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition"
                title="Mark dates you can't make"
              >
                <CalendarOff className="w-4 h-4" />
                <span className="hidden sm:inline">I Can't Make It</span>
              </button>
              <button
                onClick={() => setShowRehearsalModal(true)}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
                title="Schedule rehearsals"
              >
                <Repeat className="w-4 h-4" />
                <span className="hidden sm:inline">Add Rehearsal</span>
              </button>
              <button
                onClick={() => { setEventModalInitialDate(undefined); setShowEventModal(true); }}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                <Plus className="w-4 h-4" />
                Add Event
              </button>
            </div>
          </div>

          {/* My unavailability summary - collapsible */}
          {myUnavailability.length > 0 && (
            <div className="mb-4 bg-red-900/15 border border-red-500/20 rounded-xl p-3">
              <button
                onClick={() => setMyUnavailExpanded((v) => !v)}
                className="flex items-center justify-between w-full text-left"
              >
                <span className="text-xs font-semibold text-red-400 uppercase tracking-wide flex items-center gap-1.5">
                  <Ban className="w-3.5 h-3.5" />
                  My Unavailable Dates
                  <span className="text-gray-500 font-normal normal-case">({myUnavailability.length})</span>
                </span>
                {myUnavailExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                )}
              </button>
              {myUnavailExpanded && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {myUnavailability.map((u) => (
                    <div key={u.id} className="flex items-center gap-1.5 bg-red-900/30 border border-red-500/20 rounded-lg px-2.5 py-1.5 text-sm">
                      <span className="text-gray-300">
                        {new Date(u.start_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {u.start_date !== u.end_date && (
                          <> – {new Date(u.end_date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                        )}
                      </span>
                      {u.reason && <span className="text-gray-500 text-xs">({u.reason})</span>}
                      <button
                        onClick={() => handleDeleteUnavailability(u.id)}
                        className="text-gray-500 hover:text-red-400 transition ml-1"
                        title="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Calendar View */}
          {calendarMode === 'calendar' && (
            <BandCalendarView
              events={events}
              unavailability={unavailability}
              members={members}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              onSelectEvent={(event) => setEditingEvent(event)}
              onDeleteEvent={handleDeleteEvent}
              onDeleteUnavailability={handleDeleteUnavailability}
              onSelectDate={handleCalendarAddEvent}
            />
          )}

          {/* List View */}
          {calendarMode === 'list' && (
            <>
              {events.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No events yet. Add your first gig or rehearsal.</p>
              ) : (
                <div className="space-y-3">
                  {events
                    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                    .map((event) => {
                      const isPast = new Date(event.start_time) < new Date();
                      const notesExpanded = expandedNotes.has(event.id);
                      return (
                        <div
                          key={event.id}
                          className={`bg-gray-900 border border-gray-800 rounded-xl p-4 transition ${isPast ? 'opacity-50' : ''} hover:border-gray-700`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div
                              className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer"
                              onClick={() => setEditingEvent(event)}
                            >
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {event.visibility === 'public' ? (
                                  <Eye className="w-4 h-4 text-green-400" aria-label="Public" />
                                ) : (
                                  <EyeOff className="w-4 h-4 text-yellow-400" aria-label="Private" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium">{event.title}</h3>
                                <div className="text-sm text-gray-400">
                                  {formatDate(event.start_time)} at {formatTime(event.start_time)}
                                  {event.end_time && ` – ${formatTime(event.end_time)}`}
                                  {event.venue_name && ` | ${event.venue_name}`}
                                </div>
                                {event.venue_address && (
                                  <div className="text-xs text-gray-500 mt-0.5">{event.venue_address}</div>
                                )}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                                    event.event_type === 'gig'
                                      ? 'bg-indigo-900/50 text-indigo-300'
                                      : event.event_type === 'rehearsal'
                                        ? 'bg-emerald-900/50 text-emerald-300'
                                        : 'bg-gray-800 text-gray-400'
                                  }`}>
                                    {event.event_type}
                                  </span>
                                  {event.ticket_price && (
                                    <span className="text-xs text-gray-500">{event.ticket_price}</span>
                                  )}
                                  {event.notes_private && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedNotes((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(event.id)) next.delete(event.id);
                                          else next.add(event.id);
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-1 text-xs text-amber-400/70 hover:text-amber-400 transition"
                                      title="Toggle private notes"
                                    >
                                      <StickyNote className="w-3 h-3" />
                                      Notes
                                      {notesExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    </button>
                                  )}
                                </div>
                                {event.description && (
                                  <p className="text-gray-500 text-xs mt-1.5 line-clamp-2">{event.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-center">
                              <button
                                onClick={() => setEditingEvent(event)}
                                className="text-gray-500 hover:text-indigo-400 transition p-1"
                                title="Edit event"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDeleteEvent(event.id)}
                                  className="text-gray-500 hover:text-red-400 transition p-1"
                                  title="Delete event"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                          {/* Expanded private notes */}
                          {notesExpanded && event.notes_private && (
                            <div className="mt-3 ml-7 bg-amber-900/15 border border-amber-500/20 rounded-lg px-3 py-2">
                              <div className="flex items-center gap-1.5 text-amber-400 text-xs font-medium mb-1">
                                <StickyNote className="w-3 h-3" />
                                Private Notes
                              </div>
                              <p className="text-gray-300 text-sm whitespace-pre-wrap">{event.notes_private}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Members Tab */}
      {activeTab === 'members' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Members</h2>
            {isAdmin && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
              >
                <Plus className="w-4 h-4" />
                Invite Member
              </button>
            )}
          </div>

          {/* Pending Join Requests */}
          {isAdmin && joinRequests.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-indigo-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Join Requests ({joinRequests.length})
              </h3>
              <div className="space-y-3 mb-6">
                {joinRequests.map((req) => (
                  <div key={req.id} className="bg-gray-900 border border-indigo-500/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <span className="font-medium">
                        {req.profile?.display_name || req.profile?.email || 'Unknown user'}
                      </span>
                      {req.profile?.email && (
                        <span className="text-sm text-gray-400 ml-2">{req.profile.email}</span>
                      )}
                      {req.message && (
                        <p className="text-gray-400 text-sm mt-1">"{req.message}"</p>
                      )}
                      <p className="text-gray-500 text-xs mt-1">
                        Requested {new Date(req.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleApproveJoin(req)}
                        disabled={joinActionLoading === req.id}
                        className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectJoin(req)}
                        disabled={joinActionLoading === req.id}
                        className="flex items-center gap-1.5 bg-gray-700 hover:bg-red-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
                      >
                        <X className="w-4 h-4" />
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {members.map((member) => {
              const canEditInstruments = isAdmin || member.user_id === user?.id;
              return (
                <div key={member.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">
                          {member.display_name ?? member.profile?.display_name ?? member.email}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          member.role === 'owner'
                            ? 'bg-yellow-900/50 text-yellow-300'
                            : member.role === 'admin'
                              ? 'bg-blue-900/50 text-blue-300'
                              : 'bg-gray-800 text-gray-400'
                        }`}>
                          {member.role}
                        </span>
                        {!member.is_accepted && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-900/50 text-orange-300">
                            pending
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 mt-0.5">{member.email}</div>
                    </div>
                    {canEditInstruments && (
                      <button
                        onClick={() => setEditingInstruments(member)}
                        className="text-gray-500 hover:text-indigo-400 transition p-1.5"
                        title="Edit instruments"
                      >
                        <Guitar className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {/* Instruments display */}
                  {member.instruments && member.instruments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {member.instruments.map((inst) => (
                        <span
                          key={inst}
                          className="text-xs px-2 py-0.5 rounded-full bg-purple-900/40 text-purple-300 border border-purple-500/20"
                        >
                          {inst}
                        </span>
                      ))}
                    </div>
                  )}
                  {(!member.instruments || member.instruments.length === 0) && canEditInstruments && (
                    <button
                      onClick={() => setEditingInstruments(member)}
                      className="mt-2 text-xs text-gray-500 hover:text-indigo-400 transition flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add instruments/vocals
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Setlists Tab */}
      {activeTab === 'setlists' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Setlists</h2>
            <button
              onClick={() => setShowSetlistModal(true)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              <Plus className="w-4 h-4" />
              Create Setlist
            </button>
          </div>

          {setlists.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No setlists yet.</p>
          ) : (
            <div className="space-y-3">
              {setlists.map((setlist) => (
                <div key={setlist.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{setlist.title}</h3>
                      {setlist.is_public ? (
                        <Eye className="w-3 h-3 text-green-400" aria-label="Public" />
                      ) : (
                        <EyeOff className="w-3 h-3 text-yellow-400" aria-label="Private" />
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteSetlist(setlist.id)}
                        className="text-gray-500 hover:text-red-400 transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {setlist.songs && setlist.songs.length > 0 && (
                    <div className="text-sm text-gray-400">
                      {setlist.songs.length} song{setlist.songs.length !== 1 ? 's' : ''}
                      {' - '}
                      {setlist.songs
                        .sort((a, b) => a.position - b.position)
                        .slice(0, 3)
                        .map((s) => s.title)
                        .join(', ')}
                      {setlist.songs.length > 3 && '...'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <h2 className="text-xl font-semibold mb-6">Band Settings</h2>
          {isAdmin ? (
            <div className="space-y-4">
              <button
                onClick={() => setShowEditModal(true)}
                className="w-full bg-gray-900 border border-gray-800 rounded-xl p-4 text-left hover:border-gray-700 transition"
              >
                <h3 className="font-medium mb-1">Edit Band Profile</h3>
                <p className="text-sm text-gray-400">
                  Update your band name, description, logo, genres, and location.
                </p>
              </button>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                <h3 className="font-medium mb-1">Public Page</h3>
                <p className="text-sm text-gray-400 mb-2">
                  Your public page is at:{' '}
                  <Link to={`/band/${band.slug}`} className="text-indigo-400 hover:text-indigo-300">
                    nokset.com/band/{band.slug}
                  </Link>
                </p>
                <p className="text-sm text-gray-400">
                  Visibility: {band.is_public ? 'Public' : 'Private'}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-400">Only band owners and admins can change settings.</p>
          )}
        </div>
      )}

      {/* Modals */}
      {showInviteModal && band && user && (
        <InviteMemberModal
          bandId={band.id}
          invitedBy={user.id}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => { setShowInviteModal(false); refetchBand(); }}
        />
      )}
      {showEventModal && band && user && (
        <CreateEventModal
          bandId={band.id}
          userId={user.id}
          unavailability={unavailability}
          members={members}
          initialDate={eventModalInitialDate}
          onClose={() => { setShowEventModal(false); setEventModalInitialDate(undefined); }}
          onSuccess={() => { setShowEventModal(false); setEventModalInitialDate(undefined); refetchEvents(); }}
        />
      )}
      {showSetlistModal && band && user && (
        <CreateSetlistModal
          bandId={band.id}
          userId={user.id}
          onClose={() => setShowSetlistModal(false)}
          onSuccess={() => { setShowSetlistModal(false); refetchSetlists(); }}
        />
      )}
      {showEditModal && band && (
        <EditBandModal
          band={band}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => { setShowEditModal(false); refetchBand(); }}
        />
      )}
      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          unavailability={unavailability}
          members={members}
          onClose={() => setEditingEvent(null)}
          onSuccess={() => { setEditingEvent(null); refetchEvents(); }}
        />
      )}
      {editingInstruments && (
        <EditInstrumentsModal
          memberId={editingInstruments.id}
          memberName={editingInstruments.display_name ?? editingInstruments.profile?.display_name ?? editingInstruments.email}
          currentInstruments={editingInstruments.instruments ?? []}
          onClose={() => setEditingInstruments(null)}
          onSuccess={() => { setEditingInstruments(null); refetchBand(); }}
        />
      )}
      {showUnavailabilityModal && band && user && (
        <AddUnavailabilityModal
          bandId={band.id}
          userId={user.id}
          onClose={() => setShowUnavailabilityModal(false)}
          onSuccess={() => { setShowUnavailabilityModal(false); refetchUnavailability(); }}
        />
      )}
      {showRehearsalModal && band && user && (
        <AddRehearsalModal
          bandId={band.id}
          userId={user.id}
          unavailability={unavailability}
          members={members}
          onClose={() => setShowRehearsalModal(false)}
          onSuccess={() => { setShowRehearsalModal(false); refetchEvents(); }}
        />
      )}
    </div>
  );
}
